import React, { useEffect, useState } from 'react';
import api from '../../../config/configApi';
import { Alert, Breadcrumbs, Chip, CircularProgress, emphasize, Paper, styled, Switch } from '@mui/material';
import { MdHome } from 'react-icons/md';
import { DataGrid } from '@mui/x-data-grid';
import { localeText } from '../../../utils/webConstants';
import toast from 'react-hot-toast';
import { logError } from '../../../services/logService';
import { useAuth } from '../../../context/AuthContext';
import { useLocation } from 'react-router-dom';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    return {
        backgroundColor: theme.palette.grey[100],
        height: theme.spacing(3),
        color: (theme.vars || theme).palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(theme.palette.grey[100], 0.06),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.06),
            }),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(theme.palette.grey[100], 0.12),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.12),
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    };
});

const paginationModel = { page: 0, pageSize: 5 };

function DashboardUsuarios() {
    const { user } = useAuth();
    const location = useLocation();

    const [userList, setUserList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const response = await api.get('/api/usuarios');
                setUserList(response.data);
            } catch (err) {
                const errMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao carregar lista de usuários.';
                setError(errMessage);
                setUserList([]);
            } finally {
                setLoading(false);
            }
        }

        fetchUsers();
    }, [])

    const columns = [
        { field: 'id', headerName: 'ID', width: 70, type: 'number' },
        { field: 'email', headerName: 'EMAIL', width: 300 },
        {
            field: 'change_password', headerName: 'ALTERAR SENHA', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.change_password}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handlePassword(e, params.row.id)}
                />
            ),
        },
        {
            field: 'acesso', headerName: 'ACESSO', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.acesso}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handleAcceso(e, params.row.id)}
                />
            ),
        },
        {
            field: 'status', headerName: 'STATUS', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.status}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handleStatus(e, params.row.id)}
                />
            ),
        },
        {
            field: 'visualizar', headerName: 'VISUALIZAR', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.visualizar}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handleVisualizar(e, params.row.id)}
                />
            ),
        },
        {
            field: 'criar', headerName: 'CRIAR', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.criar}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handleCriar(e, params.row.id)}
                />
            ),
        },
        {
            field: 'atualizar', headerName: 'ATUALIZAR', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.atualizar}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handleAtualizar(e, params.row.id)}
                />
            ),
        },
        {
            field: 'deletar', headerName: 'DELETAR', width: 150, type: 'boolean',
            renderCell: (params) => (
                <Switch
                    checked={params.row.deletar}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(e) => handleDeletar(e, params.row.id)}
                />
            ),
        },
    ]

    const handlePassword = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, change_password: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando permissão de alterar senha do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { change_password: novoStatus, edit_user: user.id });                
                toast.success(`Permissão de alterar senha do usuário #${id} atualizada com sucesso!`, { id: toastId });
            } catch (err) {                
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar permissão de alterar senha';
                if (err.status !== 500) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar permissão de alterar senha do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, change_password: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro insperado ao atualizar permissão de alterar senha do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    const handleAcceso = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar!', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, acesso: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando permissão de acesso do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { acesso: novoStatus, edit_user: user.id });                
                toast.success(`Acesso do usuário #${id} alterado com sucesso`, { id: toastId });
            } catch (err) {                
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar permissão de acesso';
                if (err.status !== 500) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar acesso do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, acesso: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro inesperado ao atualizar acesso do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    const handleStatus = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar!', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, status: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando status do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { status: novoStatus, edit_user: user.id });                
                toast.success(`Status do usuário #${id} alterado com sucesso`, { id: toastId });
            } catch (err) {                
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar status';
                if (err.status === 400 || err.status === 403 || err.status === 404) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar status do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, status: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro inesperado ao atualizar status do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    const handleVisualizar = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar!', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, visualizar: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando permissão de visualizar do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { visualizar: novoStatus, edit_user: user.id });                
                toast.success(`Permissão de visualizar do usuário #${id} alterado com sucesso`, { id: toastId });
            } catch (err) {                      
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar permissão visualizar';
                if (err.status !== 500) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar permissão de visualizar do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, visualizar: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro inesperado ao atualizar permissão de visualizar do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    const handleCriar = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar!', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, criar: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando permissão de criar do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { criar: novoStatus, edit_user: user.id });                
                toast.success(`Permissão de criar do usuário #${id} alterado com sucesso`, { id: toastId });
            } catch (err) {                
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar permissão criar';
                if (err.status !== 500) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar permissão de criar do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, criar: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro inesperado ao atualizar permissão de criar do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    const handleAtualizar = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar!', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, atualizar: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando permissão de editar do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { atualizar: novoStatus, edit_user: user.id });          
                toast.success(`Permissão de editar do usuário #${id} alterado com sucesso`, { id: toastId });
            } catch (err) {                
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar permissão editar';
                if (err.status !== 500) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar permissão de editar do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, atualizar: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro inesperado ao atualizar permissão de editar do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    const handleDeletar = async (event, id) => {
        if (user.atualizar !== true) {
            toast('Seu usuário não possui permissão de editar!', { icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
        } else {
            const novoStatus = event.target.checked;            
            setUserList(prevUserList =>
                prevUserList.map(usuario =>
                    usuario.id === id ? { ...usuario, deletar: novoStatus } : usuario
                )
            )

            const toastId = toast.loading(`Atualizando permissão de deletar do usuário #${id}...`);

            try {
                const response = await api.patch(`/api/auth/update-user/${id}`, { deletar: novoStatus, edit_user: user.id });                
                toast.success(`Permissão de editar do usuário #${id} alterado com sucesso`, { id: toastId });
            } catch (err) {                
                const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar permissão editar';
                if (err.status !== 500) {
                    toast(`${errorMessage}`, { id: toastId, icon: '⚠️', style: { background: '#fff4e5', color: '#663c00' } });
                } else {
                    toast.error(`Erro inesperado ao atualizar permissão de deletar do usuário #${id}: ${errorMessage}`, { id: toastId });
                }
                setUserList(prevUserList =>
                    prevUserList.map(usuario =>
                        usuario.id === id ? { ...usuario, deletar: !novoStatus } : usuario
                    )
                )
                await logError({
                    email: user?.email || 'N/A',
                    log_erro: `Erro inesperado ao atualizar permissão de deletar do usuário #${id}: ${errorMessage}`,
                    formulario: location.pathname,
                })
            }
        }
    }

    return (
        <>
            <div className='card shadow border-0 w-100 flex-row p-4 res-col'>
                <h5 className='mb-0'>Usuários</h5>
                <Breadcrumbs
                    aria-label='breadcrumb'
                    className='ms-auto breadcrumbs_'
                >
                    <StyledBreadcrumb label='Dashboard' icon={<MdHome fontSize='small' />} />
                    <StyledBreadcrumb label='Usuários' />
                </Breadcrumbs>
            </div>
            <div className='card p-3 mt-4'>
                {loading && !error && (
                    <div className='d-flex w-100 align-items-center justify-content-center'>
                        <CircularProgress />
                    </div>
                )}
                {!loading && error && (
                    <Alert severity='error'>Erro: {error}</Alert>
                )}
                {!loading && !error && !user.acesso && (
                    <Alert severity='warning'>Seu usuário não possui permissão para visualizar!</Alert>
                )}
                {!loading && !error && userList.length === 0 && user.acesso && (
                    <p>Nenhum usuário encontrado</p>
                )}
                {!loading && !error && userList.length > 0 && user.acesso && (
                    <Paper sx={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={userList}
                            columns={columns}
                            initialState={{ pagination: { paginationModel } }}
                            pageSizeOptions={[5, 10]}
                            rowSelection={false}
                            localeText={localeText}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                }
                            }}
                        />
                    </Paper>
                )}
            </div>
        </>
    )
}

export default DashboardUsuarios;